import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import 'react-phone-input-2/lib/style.css';

import {
  BillingService,
  CreateOrgRequest,
  CreateStripeSubscriptionRequest,
  OrganizationService,
} from '@/api';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { file2Base64 } from '@/utils/file2Base64';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

// Define the form schema using zod
const formSchema = z.object({
  orgName: z.string().min(2, {
    message: 'Organization name must be at least 2 characters.',
  }),
  orgLogo: z.instanceof(File).optional().nullable(),
  orgAreaCode: z.string().optional().default(''),
});

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// Common US/Canada area codes
const AREA_CODES = [
  '201',
  '202',
  '203',
  '204',
  '205',
  '206',
  '207',
  '208',
  '209',
  '210',
  '212',
  '213',
  '214',
  '215',
  '216',
  '217',
  '218',
  '219',
  '220',
  '223',
  '224',
  '225',
  '226',
  '228',
  '229',
  '231',
  '234',
  '236',
  '239',
  '240',
  '242',
  '246',
  '248',
  '249',
  '250',
  '251',
  '252',
  '253',
  '254',
  '256',
  '260',
  '262',
  '264',
  '267',
  '268',
  '269',
  '270',
  '272',
  '276',
  '278',
  '281',
  '283',
  '284',
  '289',
  '301',
  '302',
  '303',
  '304',
  '305',
  '306',
  '307',
  '308',
  '309',
  '310',
  '312',
  '313',
  '314',
  '315',
  '316',
  '317',
  '318',
  '319',
  '320',
  '321',
  '323',
  '325',
  '326',
  '327',
  '330',
  '331',
  '332',
  '334',
  '336',
  '337',
  '339',
  '340',
  '341',
  '343',
  '345',
  '346',
  '347',
  '351',
  '352',
  '360',
  '361',
  '364',
  '365',
  '367',
  '368',
  '380',
  '385',
  '386',
  '401',
  '402',
  '403',
  '404',
  '405',
  '406',
  '407',
  '408',
  '409',
  '410',
  '412',
  '413',
  '414',
  '415',
  '416',
  '417',
  '418',
  '419',
  '423',
  '424',
  '425',
  '430',
  '431',
  '432',
  '434',
  '435',
  '437',
  '438',
  '440',
  '441',
  '442',
  '443',
  '445',
  '447',
  '450',
  '458',
  '463',
  '464',
  '468',
  '469',
  '470',
  '473',
  '474',
  '475',
  '478',
  '479',
  '480',
  '484',
  '501',
  '502',
  '503',
  '504',
  '505',
  '506',
  '507',
  '508',
  '509',
  '510',
  '512',
  '513',
  '514',
  '515',
  '516',
  '517',
  '518',
  '519',
  '520',
  '530',
  '531',
  '534',
  '539',
  '540',
  '541',
  '551',
  '557',
  '559',
  '561',
  '562',
  '563',
  '564',
  '567',
  '570',
  '571',
  '573',
  '574',
  '575',
  '579',
  '580',
  '581',
  '585',
  '586',
  '587',
  '600',
  '601',
  '602',
  '603',
  '604',
  '605',
  '606',
  '607',
  '608',
  '609',
  '610',
  '612',
  '613',
  '614',
  '615',
  '616',
  '617',
  '618',
  '619',
  '620',
  '623',
  '626',
  '628',
  '629',
  '630',
  '631',
  '636',
  '639',
  '641',
  '646',
  '647',
  '649',
  '650',
  '651',
  '656',
  '657',
  '658',
  '659',
  '660',
  '661',
  '662',
  '664',
  '667',
  '669',
  '670',
  '671',
  '672',
  '678',
  '679',
  '680',
  '681',
  '682',
  '684',
  '689',
  '700',
  '701',
  '702',
  '703',
  '704',
  '705',
  '706',
  '707',
  '708',
  '709',
  '710',
  '712',
  '713',
  '714',
  '715',
  '716',
  '717',
  '718',
  '719',
  '720',
  '721',
  '724',
  '725',
  '726',
  '727',
  '731',
  '732',
  '734',
  '737',
  '740',
  '742',
  '743',
  '747',
  '753',
  '754',
  '757',
  '758',
  '760',
  '762',
  '763',
  '764',
  '765',
  '767',
  '769',
  '770',
  '771',
  '772',
  '773',
  '774',
  '775',
  '778',
  '779',
  '780',
  '781',
  '782',
  '784',
  '785',
  '786',
  '787',
  '800',
  '801',
  '802',
  '803',
  '804',
  '805',
  '806',
  '807',
  '808',
  '809',
  '810',
  '812',
  '813',
  '814',
  '815',
  '816',
  '817',
  '818',
  '819',
  '820',
  '825',
  '828',
  '829',
  '830',
  '831',
  '832',
  '833',
  '835',
  '843',
  '844',
  '845',
  '847',
  '848',
  '849',
  '850',
  '854',
  '855',
  '856',
  '857',
  '858',
  '859',
  '860',
  '862',
  '863',
  '864',
  '865',
  '867',
  '868',
  '869',
  '870',
  '872',
  '873',
  '876',
  '877',
  '878',
  '888',
  '900',
  '901',
  '902',
  '903',
  '904',
  '905',
  '906',
  '907',
  '908',
  '909',
  '910',
  '912',
  '913',
  '914',
  '915',
  '916',
  '917',
  '918',
  '919',
  '920',
  '925',
  '928',
  '929',
  '930',
  '931',
  '934',
  '936',
  '937',
  '938',
  '939',
  '940',
  '941',
  '943',
  '945',
  '947',
  '949',
  '951',
  '952',
  '954',
  '956',
  '959',
  '970',
  '971',
  '972',
  '973',
  '975',
  '978',
  '979',
  '980',
  '984',
  '985',
  '986',
  '989',
];

function NewUserCreateOrganizationDialog({ open, setOpen }: Props) {
  // query client
  const queryClient = useQueryClient();

  // Define the form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      orgName: '',
      orgAreaCode: '',
    },
  });

  // mutation to create a new organization
  const createOrg = useMutation({
    mutationFn: async (payload: CreateOrgRequest) => {
      // call the api to create the organization
      const res = await OrganizationService.createOrganization(payload);

      return res;
    },
    onSuccess: data => {
      // invalidate queries here so that they are refetched
      queryClient.invalidateQueries({
        queryKey: ['getOrganizations'],
      });

      // close the drawer
      setOpen(false);
    },
    onError: error => {
      console.error(error);
      setOpen(false);
      toast('An error occurred', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  const createStripeSubscription = useMutation({
    mutationFn: async (payload: CreateStripeSubscriptionRequest) => {
      // call the api to create the organization
      const res = await BillingService.createStripeSubscription(payload);
      return res;
    },
    onSuccess: data => {},
    onError: error => {
      console.error(error);

      toast('An error occurred', {
        description: error.message,
        duration: 5000,
      });
    },
  });
  // Function to generate initials as an image using canvas
  const generateInitialsImage = (initials: string): string => {
    console.log(`Generating fallback image for: ${initials}`);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Set canvas size
    canvas.width = 100;
    canvas.height = 100;

    // Background color
    context!.fillStyle = '#F0F0F0';
    context!.fillRect(0, 0, canvas.width, canvas.height);

    // Text properties
    context!.font = 'bold 40px Arial';
    context!.fillStyle = '#333333';
    context!.textAlign = 'center';
    context!.textBaseline = 'middle';

    // Draw initials
    context!.fillText(initials, canvas.width / 2, canvas.height / 2);

    // Get the base64 image data and remove the prefix
    const dataUrl = canvas.toDataURL('image/png');
    return dataUrl.replace('data:image/png;base64,', '');
  };

  // Handle form submission
  async function onSubmit(values: z.infer<typeof formSchema>) {
    const payload: any = {
      name: values.orgName,
      areaCode: values.orgAreaCode,
    };

    if (values.orgLogo) {
      // Convert the uploaded logo file to base64 if provided
      const logoBase64 = await file2Base64(values.orgLogo);
      payload.logoBase64 = logoBase64;
      payload.logoFileName = values.orgLogo?.name;
      // console.log(`Logo Base64: ${payload.logoBase64}`);
    } else {
      // Generate a fallback image (initials) if no logo is provided
      const initials = values.orgName.slice(0, 2).toUpperCase().padEnd(2, 'X');
      const fallbackImage = generateInitialsImage(initials);
      // console.log(`Fallback image: ${fallbackImage}`);
      payload.logoBase64 = fallbackImage;
      payload.logoFileName = `${initials}_initials.png`;
      // console.log(`Logo Base64: ${payload.logoBase64}`);
    }

    // Call the mutation to create the organization
    const neworg = await createOrg.mutateAsync(payload);

    // create a stripe subscription for the new organization
    // await createStripeSubscription.mutateAsync({
    //   orgId: neworg.organization!.id!,
    // });
  }
  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        form.reset();
        setOpen(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Organization</DialogTitle>
          <DialogDescription>
            To get started, please create an organization. This will allow you
            to manage Workspaces, projects and collaborators.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="mt-4 space-y-8"
            >
              <FormField
                control={form.control}
                name="orgName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization name</FormLabel>
                    <FormControl>
                      <Input placeholder="Acme Inc" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter the name for the organization.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="orgLogo"
                render={({ field: { onChange }, ...field }) => (
                  <FormItem>
                    <FormLabel>Organization logo</FormLabel>
                    <FormControl>
                      <Input
                        type="file"
                        accept="image/*"
                        multiple={false}
                        {...field}
                        onChange={event =>
                          onChange(
                            event.target.files ? event.target.files[0] : null,
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Upload a logo for the organization. (this is an optional
                      field)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="orgAreaCode"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Organization area code</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            className={cn(
                              'w-full justify-between',
                              !field.value && 'text-muted-foreground',
                            )}
                          >
                            {field.value
                              ? AREA_CODES.find(code => code === field.value)
                              : 'Select area code'}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput placeholder="Search area code..." />
                          <CommandEmpty>No area code found.</CommandEmpty>
                          <CommandGroup className="max-h-[300px] overflow-auto">
                            {AREA_CODES.map(code => (
                              <CommandItem
                                value={code}
                                key={code}
                                onSelect={() => {
                                  form.setValue('orgAreaCode', code);
                                }}
                              >
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    code === field.value
                                      ? 'opacity-100'
                                      : 'opacity-0',
                                  )}
                                />
                                {code}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormDescription>
                      Enter the area code for the organization.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    form.reset();
                    setOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createOrg.isPending || createStripeSubscription.isPending
                  }
                >
                  {(createOrg.isPending ||
                    createStripeSubscription.isPending) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Submit
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NewUserCreateOrganizationDialog;
